<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';

const props = defineProps({
  from: { type: String, default: '' },
  to: { type: String, default: '' },
});

const emit = defineEmits(['update:range']);

const { t } = useI18n();
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('md');

const datePickerOpen = ref(false);
const datePickerRef = ref(null);
const triggerRef = ref(null);
const dropdownStyle = ref({});
const pendingFrom = ref('');
const pendingTo = ref('');
const calendarYear = ref(new Date().getFullYear());
const calendarMonth = ref(new Date().getMonth());
const customActive = ref(false);

const PRESETS_COL1 = [
  { key: 'today', label: 'Hoje' },
  { key: 'yest', label: 'Ontem' },
  { key: 'l7', label: 'Últimos 7 dias' },
  { key: 'l15', label: 'Últimos 15 dias' },
  { key: 'l30', label: 'Últimos 30 dias' },
  { key: 'l90', label: 'Últimos 90 dias' },
  { key: 'l180', label: 'Últimos 180 dias' },
  { key: 'l360', label: 'Últimos 360 dias' },
  { key: 'all', label: 'Todo período' },
];

const PRESETS_COL2 = [
  { key: 'tw', label: 'Essa semana' },
  { key: 'lw', label: 'Semana passada' },
  { key: 'tm', label: 'Esse mês' },
  { key: 'lm', label: 'Mês passado' },
  { key: 'tq', label: 'Esse trimestre' },
  { key: 'lq', label: 'Trimestre passado' },
  { key: 'ty', label: 'Esse ano' },
  { key: 'ly', label: 'Ano passado' },
  { key: 'custom', label: 'Personalizado' },
];

function localISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return localISO(d);
}

function startOfWeek(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function startOfQuarter(date) {
  const q = Math.floor(date.getMonth() / 3);
  return new Date(date.getFullYear(), q * 3, 1);
}

function endOfQuarter(date) {
  const q = Math.floor(date.getMonth() / 3);
  return new Date(date.getFullYear(), q * 3 + 3, 0);
}

function getPresetRange(key) {
  const now = new Date();
  const today = localISO(now);
  switch (key) {
    case 'today':
      return { from: today, to: today };
    case 'yest': {
      const d = addDays(-1);
      return { from: d, to: d };
    }
    case 'l7':
      return { from: addDays(-6), to: today };
    case 'l15':
      return { from: addDays(-14), to: today };
    case 'l30':
      return { from: addDays(-29), to: today };
    case 'l90':
      return { from: addDays(-89), to: today };
    case 'l180':
      return { from: addDays(-179), to: today };
    case 'l360':
      return { from: addDays(-359), to: today };
    case 'all':
      return { from: '2000-01-01', to: today };
    case 'tw':
      return { from: localISO(startOfWeek(now)), to: today };
    case 'lw': {
      const s = startOfWeek(
        new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7)
      );
      const e = new Date(s);
      e.setDate(s.getDate() + 6);
      return { from: localISO(s), to: localISO(e) };
    }
    case 'tm':
      return {
        from: localISO(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: today,
      };
    case 'lm': {
      const f = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { from: localISO(f), to: localISO(endOfMonth(f)) };
    }
    case 'tq':
      return { from: localISO(startOfQuarter(now)), to: today };
    case 'lq': {
      const f = startOfQuarter(
        new Date(now.getFullYear(), now.getMonth() - 3, 1)
      );
      return { from: localISO(f), to: localISO(endOfQuarter(f)) };
    }
    case 'ty':
      return { from: `${now.getFullYear()}-01-01`, to: today };
    case 'ly':
      return {
        from: `${now.getFullYear() - 1}-01-01`,
        to: `${now.getFullYear() - 1}-12-31`,
      };
    default:
      return null;
  }
}

const activePreset = computed(() => {
  return (
    [...PRESETS_COL1, ...PRESETS_COL2.filter(p => p.key !== 'custom')].find(
      p => {
        const r = getPresetRange(p.key);
        return r && r.from === props.from && r.to === props.to;
      }
    ) ?? null
  );
});

function formatDateLabel(iso) {
  if (!iso) return '';
  const parts = iso.split('-');
  const months = [
    'jan',
    'fev',
    'mar',
    'abr',
    'mai',
    'jun',
    'jul',
    'ago',
    'set',
    'out',
    'nov',
    'dez',
  ];
  return `${parseInt(parts[2], 10)} ${months[parseInt(parts[1], 10) - 1]}`;
}

const dateButtonLabel = computed(() => {
  if (activePreset.value) return activePreset.value.label;
  if (!props.from && !props.to) {
    return t('KANBAN.FILTERS.DATE_RANGE_PLACEHOLDER');
  }
  return `${formatDateLabel(props.from)} – ${formatDateLabel(props.to)}`;
});

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];
const DAY_NAMES = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const calendarDays = computed(() => {
  const year = calendarYear.value;
  const month = calendarMonth.value;
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let i = 0; i < firstDay; i += 1) days.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) days.push(d);
  return days;
});

function calISO(d) {
  return `${calendarYear.value}-${String(calendarMonth.value + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function dayClass(d) {
  if (!d) return '';
  const iso = calISO(d);
  const from = pendingFrom.value || props.from || '';
  const to = pendingTo.value || props.to || '';
  const today = localISO(new Date());
  const isStart = iso === from;
  const isEnd = iso === to && to !== from;
  const inRange = !!(from && to && iso > from && iso < to);
  const isToday = iso === today;
  return {
    'cursor-pointer transition-colors': true,
    'text-white bg-n-brand font-semibold rounded-lg': isStart || isEnd,
    'bg-n-alpha-2 text-n-brand rounded-none': inRange,
    'hover:bg-n-alpha-1 rounded-lg': !isStart && !isEnd && !inRange,
    'text-n-slate-12': !isStart && !isEnd && !inRange && !isToday,
    'font-bold text-n-brand': isToday && !isStart && !isEnd && !inRange,
  };
}

function selectDay(d) {
  if (!d) return;
  const iso = calISO(d);
  if (!pendingFrom.value || (pendingFrom.value && pendingTo.value)) {
    pendingFrom.value = iso;
    pendingTo.value = '';
    customActive.value = true;
  } else if (iso < pendingFrom.value) {
    pendingTo.value = pendingFrom.value;
    pendingFrom.value = iso;
    emit('update:range', { from: pendingFrom.value, to: pendingTo.value });
    datePickerOpen.value = false;
  } else {
    pendingTo.value = iso;
    emit('update:range', { from: pendingFrom.value, to: pendingTo.value });
    datePickerOpen.value = false;
  }
}

function prevMonth() {
  if (calendarMonth.value === 0) {
    calendarMonth.value = 11;
    calendarYear.value -= 1;
  } else {
    calendarMonth.value -= 1;
  }
}
function nextMonth() {
  if (calendarMonth.value === 11) {
    calendarMonth.value = 0;
    calendarYear.value += 1;
  } else {
    calendarMonth.value += 1;
  }
}
function prevYear() {
  calendarYear.value -= 1;
}
function nextYear() {
  calendarYear.value += 1;
}

function applyPreset(key) {
  if (key === 'custom') {
    customActive.value = true;
    pendingFrom.value = props.from || '';
    pendingTo.value = props.to || '';
    return;
  }
  const r = getPresetRange(key);
  if (r) {
    emit('update:range', r);
    datePickerOpen.value = false;
    customActive.value = false;
  }
}

function updateDropdownPosition() {
  if (isMobile.value || !triggerRef.value) return;
  const rect = triggerRef.value.getBoundingClientRect();
  const dropdownWidth = 560;
  const margin = 8;
  const overflowsRight = rect.left + dropdownWidth > window.innerWidth - margin;
  dropdownStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + margin}px`,
    ...(overflowsRight
      ? { right: `${window.innerWidth - rect.right}px` }
      : { left: `${rect.left}px` }),
  };
}

function openDatePicker() {
  pendingFrom.value = props.from || '';
  pendingTo.value = props.to || '';
  customActive.value = !activePreset.value;
  const d = new Date(props.to || new Date());
  calendarYear.value = d.getFullYear();
  calendarMonth.value = d.getMonth();
  updateDropdownPosition();
  datePickerOpen.value = true;
}

function onClickOutside(e) {
  if (
    datePickerRef.value &&
    !datePickerRef.value.contains(e.target) &&
    !e.target.closest('.date-range-picker-dropdown')
  ) {
    datePickerOpen.value = false;
  }
}
onMounted(() => {
  document.addEventListener('mousedown', onClickOutside);
  window.addEventListener('resize', updateDropdownPosition);
});
onUnmounted(() => {
  document.removeEventListener('mousedown', onClickOutside);
  window.removeEventListener('resize', updateDropdownPosition);
});

watch(
  () => [props.from, props.to],
  () => {
    if (!datePickerOpen.value) return;
    pendingFrom.value = props.from || '';
    pendingTo.value = props.to || '';
  }
);
</script>

<template>
  <div ref="datePickerRef" class="relative">
    <button
      ref="triggerRef"
      class="w-full flex items-center gap-1.5 border border-n-weak rounded-md px-2 py-1.5 text-sm bg-n-surface-2 text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
      @click="openDatePicker"
    >
      <span class="i-lucide-calendar size-3.5 text-n-slate-11" />
      <span class="truncate">{{ dateButtonLabel }}</span>
      <span class="i-lucide-chevron-down size-3 text-n-slate-10 ml-auto" />
    </button>

    <Teleport to="body">
      <div
        v-if="datePickerOpen"
        class="date-range-picker-dropdown fixed inset-0 z-50 md:inset-auto bg-n-background border border-n-weak md:rounded-xl shadow-xl flex flex-col md:flex-row overflow-hidden md:min-w-[560px]"
        :style="!isMobile ? dropdownStyle : {}"
        @click.stop
      >
        <div
          v-if="isMobile"
          class="flex items-center justify-between p-3 border-b border-n-weak"
        >
          <span class="text-sm font-semibold text-n-slate-12">
            {{ $t('KANBAN.FILTERS.DATE_RANGE_SELECT_PERIOD') }}
          </span>
          <button
            class="p-1.5 text-n-slate-9 hover:text-n-slate-12 transition-colors"
            @click="datePickerOpen = false"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div
          class="flex flex-col md:flex-row overflow-y-auto md:overflow-visible flex-1 md:contents"
        >
          <div class="p-3 flex flex-col gap-1 shrink-0">
            <div class="flex gap-0 justify-around md:justify-start">
              <div class="flex flex-col gap-0.5">
                <button
                  v-for="p in PRESETS_COL1"
                  :key="p.key"
                  class="text-left px-3 py-1.5 text-xs rounded-lg transition-colors whitespace-nowrap"
                  :class="
                    !customActive && activePreset?.key === p.key
                      ? 'bg-n-alpha-2 text-n-brand font-semibold'
                      : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
                  "
                  @click="applyPreset(p.key)"
                >
                  {{ p.label }}
                </button>
              </div>
              <div class="flex flex-col gap-0.5">
                <template v-for="p in PRESETS_COL2" :key="p.key">
                  <button
                    class="text-left px-3 py-1.5 text-xs rounded-lg transition-colors whitespace-nowrap"
                    :class="
                      p.key === 'custom'
                        ? customActive
                          ? 'bg-n-alpha-2 text-n-brand font-semibold'
                          : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
                        : !customActive && activePreset?.key === p.key
                          ? 'bg-n-alpha-2 text-n-brand font-semibold'
                          : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
                    "
                    @click="applyPreset(p.key)"
                  >
                    {{ p.label }}
                  </button>
                </template>
              </div>
            </div>
          </div>

          <div class="p-3 flex flex-col gap-2 flex-1">
            <div class="flex items-center justify-center gap-1 text-xs">
              <button
                class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-slate-12"
                :aria-label="$t('KANBAN.FILTERS.DATE_RANGE_PREV_YEAR')"
                @click="prevYear"
              >
                <span class="i-lucide-chevrons-left size-3.5" />
              </button>
              <button
                class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-slate-12"
                :aria-label="$t('KANBAN.FILTERS.DATE_RANGE_PREV_MONTH')"
                @click="prevMonth"
              >
                <span class="i-lucide-chevron-left size-3.5" />
              </button>
              <span
                class="text-n-slate-12 font-semibold min-w-[120px] text-center"
              >
                {{ MONTH_NAMES[calendarMonth] }} {{ calendarYear }}
              </span>
              <button
                class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-slate-12"
                :aria-label="$t('KANBAN.FILTERS.DATE_RANGE_NEXT_MONTH')"
                @click="nextMonth"
              >
                <span class="i-lucide-chevron-right size-3.5" />
              </button>
              <button
                class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-slate-12"
                :aria-label="$t('KANBAN.FILTERS.DATE_RANGE_NEXT_YEAR')"
                @click="nextYear"
              >
                <span class="i-lucide-chevrons-right size-3.5" />
              </button>
            </div>

            <div class="grid grid-cols-7">
              <div
                v-for="d in DAY_NAMES"
                :key="d"
                class="h-9 w-9 flex items-center justify-center text-[10px] text-n-slate-10 font-medium"
              >
                {{ d }}
              </div>
            </div>

            <div class="grid grid-cols-7 flex-1 content-start">
              <div
                v-for="(d, i) in calendarDays"
                :key="i"
                :class="d ? dayClass(d) : ''"
                class="h-9 w-9 flex items-center justify-center text-xs"
                @click="d && selectDay(d)"
              >
                {{ d || '' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
